// =============================================================================
// Edge Functions Catalog — embedded as a tab inside /admin/infrastructure
//
// Hand-maintained catalog of every Supabase edge function across the App
// project and the Platform project. The data lives in
// src/data/edgeFunctionsCatalog.ts; this is a thin filter/search/render
// shell that the Infrastructure page mounts as its first tab.
//
// Why a reference catalog (not auto-discovery)?
//   - Supabase Management API can list function NAMES, but not what they do
//     or which UI / cron / webhook calls them.
//   - Descriptions + caller-context only exist in the source files (header
//     comments) — extracting them at runtime is brittle.
//   - The catalog file is small (~30KB) and changes only when a function is
//     added or renamed — easy to keep in sync.
//
// History: originally lived at its own /admin/edge-functions route. Merged
// into Infrastructure once it became clear the two answer complementary
// questions about the same domain (catalog = "what is this?" / Scheduled
// + Secrets + Audit = "what's it doing right now?"). One nav entry, one
// page, four tabs — less context-switching for operators.
// =============================================================================

import { useMemo, useState } from 'react';
import {
  Zap, Search, X, AppWindow, Server,
  Clock, Webhook, MousePointer2, Database, Globe, Link2,
} from 'lucide-react';
import { cn } from '@/utils/helpers';
import {
  EDGE_FUNCTIONS,
  EDGE_FN_CATEGORIES,
  type EdgeFnEntry,
  type EdgeFnProject,
  type EdgeFnTrigger,
  type EdgeFnCategory,
} from '@/data/edgeFunctionsCatalog';

// ── Trigger icon + colour ────────────────────────────────────────────────────
//
// One glance at the column should tell you "is this a cron, a webhook, a user
// click, etc." — the icon is paired with a soft-tint chip so failed-eye scan
// of the table groups things naturally.

function TriggerChip({ trigger }: { trigger: EdgeFnTrigger }) {
  const cfg: Record<EdgeFnTrigger, { label: string; icon: typeof Zap; cls: string }> = {
    'user-action': { label: 'User action', icon: MousePointer2, cls: 'bg-sky-500/10 text-sky-700 dark:text-sky-300' },
    'cron':        { label: 'Cron',        icon: Clock,         cls: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300' },
    'webhook':     { label: 'Webhook',     icon: Webhook,       cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' },
    'db-trigger':  { label: 'DB trigger',  icon: Database,      cls: 'bg-rose-500/10 text-rose-700 dark:text-rose-300' },
    'public':      { label: 'Public',      icon: Globe,         cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' },
    'internal':    { label: 'Internal',    icon: Link2,         cls: 'bg-surface-200 text-surface-700 dark:text-surface-300' },
  };
  const c = cfg[trigger];
  const Icon = c.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap', c.cls)}>
      <Icon size={10} />
      {c.label}
    </span>
  );
}

function ProjectChip({ project }: { project: EdgeFnProject }) {
  const isApp = project === 'app';
  const Icon = isApp ? AppWindow : Server;
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold',
      isApp ? 'bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300'
            : 'bg-violet-500/10 text-violet-700 dark:text-violet-300',
    )}>
      <Icon size={10} />
      {isApp ? 'App' : 'Platform'}
    </span>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

/** Catalog body — exported for use as a tab inside InfrastructurePage.
 *  No page chrome (no h1, no max-width wrapper, no outer padding) since
 *  the host page already provides those. Just the filter bar + table +
 *  footer note. */
export function EdgeFunctionsCatalogTab() {
  // Filter state. Empty string / null means "all". Search is case-insensitive
  // against name + description + caller — anything visible to the operator.
  const [search, setSearch]       = useState('');
  const [project, setProject]     = useState<EdgeFnProject | 'all'>('all');
  const [category, setCategory]   = useState<EdgeFnCategory | 'all'>('all');
  const [trigger, setTrigger]     = useState<EdgeFnTrigger | 'all'>('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return EDGE_FUNCTIONS.filter(fn => {
      if (project  !== 'all' && fn.project  !== project)  return false;
      if (category !== 'all' && fn.category !== category) return false;
      if (trigger  !== 'all' && fn.trigger  !== trigger)  return false;
      if (q && !(
        fn.name.toLowerCase().includes(q) ||
        fn.description.toLowerCase().includes(q) ||
        fn.caller.toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [search, project, category, trigger]);

  // Count breakdown for the header — same filter applied, but split by project
  // so the chip toggles tell the operator the size of each pool.
  const stats = useMemo(() => {
    const appCount      = EDGE_FUNCTIONS.filter(fn => fn.project === 'app').length;
    const platformCount = EDGE_FUNCTIONS.filter(fn => fn.project === 'platform').length;
    return { total: EDGE_FUNCTIONS.length, app: appCount, platform: platformCount };
  }, []);

  const clearFilters = () => {
    setSearch('');
    setProject('all');
    setCategory('all');
    setTrigger('all');
  };
  const anyFilterActive = !!search || project !== 'all' || category !== 'all' || trigger !== 'all';

  return (
    <div className="space-y-4">
      {/* ── Catalog summary line ──────────────────────────────────────── */}
      <div className="text-sm text-surface-500">
        Reference catalog of every Supabase edge function across both projects.{' '}
        <strong className="text-surface-700">{stats.total}</strong> total
        {' '}(<strong className="text-fuchsia-700">{stats.app} App</strong>
        {' '}+ <strong className="text-violet-700">{stats.platform} Platform</strong>).
      </div>

      {/* ── Filter bar ─────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-overlay/[0.06] bg-surface-0 p-3 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, description, caller…"
              className="w-full pl-9 pr-3 py-1.5 rounded-lg text-sm bg-surface-50 border border-overlay/[0.06] focus:outline-none focus:ring-2 focus:ring-sky-400/40"
            />
          </div>

          {/* Project pill toggle */}
          <div className="inline-flex p-0.5 rounded-lg bg-surface-100 border border-overlay/[0.06]">
            {([
              { v: 'all' as const,      label: 'All' },
              { v: 'app' as const,      label: 'App' },
              { v: 'platform' as const, label: 'Platform' },
            ]).map(opt => (
              <button
                key={opt.v}
                onClick={() => setProject(opt.v)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-[12px] font-semibold transition-colors',
                  project === opt.v
                    ? 'bg-surface-0 text-surface-900 shadow-sm'
                    : 'text-surface-500 hover:text-surface-700',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Category dropdown */}
          <select
            value={category}
            onChange={e => setCategory(e.target.value as EdgeFnCategory | 'all')}
            className="px-2.5 py-1.5 rounded-lg text-[12px] font-medium bg-surface-50 border border-overlay/[0.06] focus:outline-none focus:ring-2 focus:ring-sky-400/40"
          >
            <option value="all">All categories</option>
            {EDGE_FN_CATEGORIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          {/* Trigger dropdown */}
          <select
            value={trigger}
            onChange={e => setTrigger(e.target.value as EdgeFnTrigger | 'all')}
            className="px-2.5 py-1.5 rounded-lg text-[12px] font-medium bg-surface-50 border border-overlay/[0.06] focus:outline-none focus:ring-2 focus:ring-sky-400/40"
          >
            <option value="all">All triggers</option>
            <option value="user-action">User action</option>
            <option value="cron">Cron</option>
            <option value="webhook">Webhook</option>
            <option value="db-trigger">DB trigger</option>
            <option value="public">Public</option>
            <option value="internal">Internal</option>
          </select>

          {anyFilterActive && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium text-surface-600 hover:bg-surface-100"
              title="Clear all filters"
            >
              <X size={11} />
              Clear
            </button>
          )}
        </div>

        <div className="text-[11px] text-surface-500">
          Showing <strong className="text-surface-700">{filtered.length}</strong>{' '}
          of <strong>{stats.total}</strong> functions
          {anyFilterActive && ' (filtered)'}
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-overlay/[0.06] bg-surface-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-50/60 text-surface-500 text-[11px] uppercase tracking-widest sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-semibold w-[1%] whitespace-nowrap">Name</th>
                <th className="text-left px-3 py-2 font-semibold w-[1%] whitespace-nowrap">Project</th>
                <th className="text-left px-3 py-2 font-semibold w-[1%] whitespace-nowrap">Category</th>
                <th className="text-left px-3 py-2 font-semibold w-[1%] whitespace-nowrap">Trigger</th>
                <th className="text-left px-3 py-2 font-semibold">What it does</th>
                <th className="text-left px-3 py-2 font-semibold">Called by</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-surface-500">
                    No functions match these filters.
                  </td>
                </tr>
              ) : filtered.map(fn => (
                <FunctionRow key={`${fn.project}:${fn.name}`} fn={fn} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Footer note ────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-dashed border-overlay/[0.10] bg-surface-50/50 px-4 py-3 text-[11px] text-surface-500">
        Catalog is hand-maintained in{' '}
        <code className="font-mono text-surface-700">src/data/edgeFunctionsCatalog.ts</code>.
        If you add, rename, or remove an edge function, update that file in the same PR.
      </div>
    </div>
  );
}

// ── Row ─────────────────────────────────────────────────────────────────────

function FunctionRow({ fn }: { fn: EdgeFnEntry }) {
  // Build the Supabase Dashboard logs URL for this function. Both projects
  // use the same path pattern, so we link straight to the function's logs
  // pane — operators investigating "did this fire?" land in the right place.
  //
  // The project refs are stable, but we don't want to hardcode them in
  // every cell — leaving the link as the function NAME and letting the
  // operator paste it manually into Supabase Dashboard works. (We could
  // surface APP_SUPABASE_URL + Platform URL from env to build deep links
  // — left as a future enhancement.)
  return (
    <tr className="border-t border-overlay/[0.04] align-top hover:bg-surface-50/40">
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="font-mono text-[12px] font-semibold text-surface-900">{fn.name}</div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap"><ProjectChip project={fn.project} /></td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium text-surface-600 bg-surface-100">
          {fn.category}
        </span>
      </td>
      <td className="px-3 py-2 whitespace-nowrap"><TriggerChip trigger={fn.trigger} /></td>
      <td className="px-3 py-2 text-[12px] text-surface-700 max-w-[28rem]">{fn.description}</td>
      <td className="px-3 py-2 text-[12px] text-surface-600 max-w-[22rem]">{fn.caller}</td>
    </tr>
  );
}

export default EdgeFunctionsCatalogTab;
